import { Children, type CSSProperties, type ReactNode, useCallback, useLayoutEffect, useRef, useState } from "react";
import type { VirtualizerHandle } from "virtua";
import {
  fadeStyle,
  scrollSurfaceStyle,
  useAfterPaint,
  useFloatingScrollbar,
  useResizeObserver,
  useScrollActivity,
  useSubtreeMeasure,
} from "@tellescope/react-hooks";
import type {
  PaginatedListAnchor,
  PaginatedListEdgeState,
  PaginatedListScrollbar,
  PaginatedListScrollOptions,
} from "./types";

interface UsePaginatedListOptions {
  onBottomReached?: () => void;
  onTopReached?: () => void;
  onFocusWithin?: (edges: PaginatedListEdgeState) => void;
  onClickWithin?: (edges: PaginatedListEdgeState) => void;
  threshold: number;
  isLoading: boolean;
  scrollOptions?: PaginatedListScrollOptions;
  anchor: PaginatedListAnchor;
  /** Needed to tell a prepend from any other change, which scroll state alone cannot report. */
  children: ReactNode;
}

interface ChildKeys {
  first: string | null;
  last: string | null;
  count: number;
}

/**
 * A child's key, for telling a prepend from an append. `Children.toArray` keys every entry it
 * returns, including the strings and numbers that carry no key of their own, so a list of those
 * still compares — by position rather than identity, which is the best available for unkeyed content.
 */
const getChildKey = (child: ReactNode): string | null =>
  child && typeof child === "object" && "key" in child ? child.key : null;

/** Whether an element scrolls and its end is still below the fold. */
const isScrolledShort = (element: HTMLElement): boolean => {
  const { scrollTop, scrollHeight, clientHeight } = element;
  return scrollHeight > clientHeight + 1 && scrollTop + clientHeight < scrollHeight - 1;
};

export const usePaginatedList = ({
  onBottomReached,
  onTopReached,
  onFocusWithin,
  onClickWithin,
  threshold,
  isLoading,
  scrollOptions,
  anchor,
  children,
}: UsePaginatedListOptions) => {
  const scrollElement = scrollOptions?.element;
  const scrollbar: PaginatedListScrollbar = scrollOptions?.type ?? "native";
  const fadeWhenClipped = scrollOptions?.fadeWhenClipped ?? false;
  const measureStartMargin = scrollOptions?.measureStartMargin ?? false;

  const virtualizerRef = useRef<VirtualizerHandle>(null);
  const topReachedRef = useRef(false);
  const bottomReachedRef = useRef(false);
  const scrollerElementRef = useRef<HTMLDivElement | null>(null);
  const { isScrolling, onScroll: trackScrollActivity } = useScrollActivity();
  const { scrollContainerRef, thumbRef, handleScroll: updateThumb } = useFloatingScrollbar();

  // `element` absent from `scrollOptions` entirely and `element: null` mean different things: the
  // former is "this list scrolls itself", the latter is "an ancestor scrolls, but its node has not
  // attached yet" — which must not fall back to self-scrolling, or the list would scroll on its own
  // for one render and then stop.
  const ownsScrolling = scrollOptions === undefined || !("element" in scrollOptions);
  const showFloatingThumb = ownsScrolling && scrollbar === "floating";

  // `virtua` takes a RefObject rather than an element, so the external scroller is mirrored into one.
  // Assigned during render so the virtualizer sees the current node on the same pass it is given, and
  // kept as a stable object so passing it never re-subscribes anything.
  const externalScrollRef = useRef<HTMLElement | null>(null);
  externalScrollRef.current = scrollElement ?? null;

  const { ref: clipRef, value: measuredClipped } = useResizeObserver<boolean>(isScrolledShort, false);
  const [scrolledClipped, setScrolledClipped] = useState<boolean>();
  // The fade needs this list to own its scroller — with an external `element` the offsets belong to a
  // container this component does not render. Gating the tracking as well as the read keeps
  // `handleScroll` from writing state nothing can observe, which would re-render the rows mid-scroll.
  const tracksClipping = ownsScrolling && fadeWhenClipped;
  const isClipped = tracksClipping && (scrolledClipped ?? measuredClipped);

  const startMarginRef = useRef<HTMLDivElement | null>(null);
  const shouldMeasureStartMargin = measureStartMargin && !ownsScrolling;

  // Memoized because `clipRef` tracks element *identity*: an inline callback makes React detach
  // (`null`) and re-attach the node every render, re-running the resize observer's effect each pass.
  const attachScroller = useCallback(
    (element: HTMLDivElement | null) => {
      startMarginRef.current = element;
      scrollerElementRef.current = element;
      if (!ownsScrolling) return;
      scrollContainerRef.current = element;
      clipRef.current = element;
    },
    [ownsScrolling, scrollContainerRef, clipRef]
  );

  const { value: startMargin } = useSubtreeMeasure(
    scrollElement,
    (container) => {
      const element = startMarginRef.current;
      if (!element) return 0;
      const offset = element.getBoundingClientRect().top - container.getBoundingClientRect().top;
      return Math.max(0, Math.round(offset + container.scrollTop));
    },
    0,
    { enabled: shouldMeasureStartMargin }
  );

  const isAnchoredToEnd = anchor === "end";

  // `shift` is read by the virtualizer during its own render, so it has to be derived here rather
  // than in an effect. A grown list whose FIRST key changed while its last stayed put is a prepend —
  // the only case `shift` is safe for; virtua's docs warn that shifting for mid/end insertions
  // misbehaves. Index keys break this outright (prepending renumbers every child), which is why
  // `anchor="end"` documents stable keys as a requirement rather than a convention.
  //
  // The previous keys are recorded in the effect below, not during render: React may render a pass it
  // then discards, and a ref written during render would record those keys as if they had committed.
  const childArray = isAnchoredToEnd ? Children.toArray(children) : [];
  const keys: ChildKeys = {
    first: getChildKey(childArray[0]),
    last: getChildKey(childArray[childArray.length - 1]),
    count: childArray.length,
  };
  const previousKeysRef = useRef<ChildKeys>({ first: null, last: null, count: 0 });
  const previousKeys = previousKeysRef.current;
  const isPrepending =
    isAnchoredToEnd &&
    keys.count > previousKeys.count &&
    keys.first !== previousKeys.first &&
    keys.last === previousKeys.last;

  useLayoutEffect(() => {
    previousKeysRef.current = keys;
  });

  /**
   * Following the end while the reader is already there happens in `EndAnchoredContent`, which can
   * see each growth as it lands. This is the other case, on request: the reader is scrolled up and
   * the consumer knows the view should come back down anyway. A plain scroll to the current end is
   * enough even when the children below are still unmeasured — every correction that follows is a
   * growth the reader is at the end of, which the content element then follows.
   */
  const scrollToEnd = useCallback(() => {
    const scroller = scrollerElementRef.current;
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  }, []);

  const handleTopReached = useCallback(() => {
    if (!topReachedRef.current && onTopReached) {
      topReachedRef.current = true;
      bottomReachedRef.current = false;
      onTopReached();
    }
  }, [onTopReached]);

  const handleBottomReached = useCallback(() => {
    if (!bottomReachedRef.current && onBottomReached) {
      bottomReachedRef.current = true;
      topReachedRef.current = false;
      onBottomReached();
    }
  }, [onBottomReached]);

  const handleScroll = useCallback(
    (offset: number) => {
      const virtualizer = virtualizerRef.current;
      if (!virtualizer) return;

      const { scrollSize, viewportSize } = virtualizer;

      if (offset <= threshold) {
        handleTopReached();
      } else {
        topReachedRef.current = false;
      }

      if (offset - scrollSize + viewportSize >= -threshold) {
        handleBottomReached();
      } else {
        bottomReachedRef.current = false;
      }

      if (scrollbar === "hidden-until-scroll") trackScrollActivity();

      if (scrollbar === "floating") updateThumb();
      if (tracksClipping) {
        setScrolledClipped(scrollSize > viewportSize && offset - scrollSize + viewportSize < -1);
      }
    },
    [threshold, handleTopReached, handleBottomReached, scrollbar, trackScrollActivity, updateThumb, tracksClipping]
  );

  const handleFocusCapture = useCallback(() => {
    onFocusWithin?.({ isAtTop: topReachedRef.current, isAtBottom: bottomReachedRef.current });
  }, [onFocusWithin]);

  const handleClickCapture = useCallback(() => {
    onClickWithin?.({ isAtTop: topReachedRef.current, isAtBottom: bottomReachedRef.current });
  }, [onClickWithin]);

  // Content short enough to fit its viewport never scrolls, so `virtua` — which reports only on real
  // scroll events — never fires, and the end of the list is never reached. A first page that
  // underfills its container would otherwise strand every page after it. `handleBottomReached`'s
  // latch keeps this to one call per settled content. A bottom-anchored list paginates upward, so
  // its short-first-page case strands the pages above it unless the start is reported reached too.
  //
  // Two frames because the virtualizer publishes its own measurement a frame after paint: read any
  // earlier and `viewportSize` is still 0, which the guard below (correctly) declines to act on.
  useAfterPaint(
    () => {
      const virtualizer = virtualizerRef.current;
      if (!virtualizer) return;
      const { scrollSize, viewportSize } = virtualizer;
      if (viewportSize === 0 || scrollSize > viewportSize) return;

      handleBottomReached();
      if (isAnchoredToEnd) handleTopReached();
    },
    [handleBottomReached, handleTopReached, isAnchoredToEnd],
    { enabled: !isLoading, frames: 2 }
  );

  const surfaceStyle: CSSProperties | undefined = ownsScrolling
    ? scrollbar === "floating"
      ? scrollSurfaceStyle({ faded: isClipped })
      : fadeStyle({ faded: isClipped })
    : undefined;

  return {
    virtualizerRef,
    attachScroller,
    thumbRef,
    handleScroll,
    handleFocusCapture,
    handleClickCapture,
    shift: isPrepending,
    isAnchoredToEnd,
    scrollToEnd,
    externalScrollRef,
    startMargin: shouldMeasureStartMargin ? startMargin : undefined,
    ownsScrolling,
    showFloatingThumb,
    scrollbar,
    isScrolling,
    surfaceStyle,
  };
};
